import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CookieBanner from '../../components/feature/CookieBanner';
import StructuredData from '../../components/feature/StructuredData';
import ScrollToTop from '../../components/feature/ScrollToTop';

const FaqPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  useEffect(() => {
    document.title = 'Preguntas Frecuentes - Vaciado de Pisos Barcelona';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Resuelve tus dudas sobre el vaciado de pisos en Barcelona. Precios, plazos, gestión de residuos y más. BIOTRAUMA BARCELONA responde a tus preguntas.'
      );
    }

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Preguntas Frecuentes - Vaciado de Pisos Barcelona');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:title');
      meta.content = 'Preguntas Frecuentes - Vaciado de Pisos Barcelona';
      document.head.appendChild(meta);
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute(
        'content',
        'Resuelve tus dudas sobre el vaciado de pisos en Barcelona. Precios, plazos, gestión de residuos y más. BIOTRAUMA BARCELONA responde a tus preguntas.'
      );
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:description');
      meta.content = 'Resuelve tus dudas sobre el vaciado de pisos en Barcelona. Precios, plazos, gestión de residuos y más. BIOTRAUMA BARCELONA responde a tus preguntas.';
      document.head.appendChild(meta);
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', 'https://barcelona.biotrauma.es/faq');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:url');
      meta.content = 'https://barcelona.biotrauma.es/faq';
      document.head.appendChild(meta);
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://barcelona.biotrauma.es/faq');
    }
  }, []);

  const faqs = [
    {
      question: '¿Cuánto cuesta vaciar un piso en Barcelona?',
      answer: 'El precio depende de varios factores: metros cuadrados del inmueble, cantidad de enseres a retirar, accesibilidad (planta, ascensor), y ubicación exacta. Realizamos presupuestos personalizados sin compromiso tras ver fotos por WhatsApp o hacer una visita al inmueble. Como referencia, un piso estándar de 70-80m² puede costar entre 400-800€, pero cada caso es único. Consulta todos nuestros servicios en nuestra página de servicios.',
    },
    {
      question: '¿Cuál es el servicio mínimo que ofrecéis?',
      answer: 'Nuestro servicio mínimo es una furgoneta completa. No realizamos recogidas de objetos sueltos como una sola mesa, silla o electrodoméstico. Este mínimo nos permite mantener precios competitivos y optimizar nuestros recursos. Si necesitas retirar solo algunos objetos, te recomendamos acumularlos hasta completar al menos media furgoneta.',
    },
    {
      question: '¿En cuánto tiempo pueden vaciar un piso?',
      answer: 'Depende del tamaño y la cantidad de enseres. Un piso estándar de 70-80m² con mobiliario normal se vacía en 4-6 horas de trabajo. Pisos más grandes de 100-120m² pueden requerir entre 8-12 horas (1-2 días). Casas unifamiliares o locales comerciales grandes pueden necesitar 2-3 días. Te daremos un tiempo estimado en el presupuesto.',
    },
    {
      question: '¿Qué hacéis con los muebles y enseres retirados?',
      answer: 'Gestionamos todos los residuos de forma responsable en puntos limpios autorizados por el Ayuntamiento de Barcelona. Los objetos en buen estado (muebles, ropa, electrodomésticos funcionales) se donan a entidades sociales y ONGs cuando es posible. Los residuos se clasifican y reciclan según normativa. Entregamos certificado de gestión de residuos si lo necesitas. Conoce más sobre nuestra empresa y valores.',
    },
    {
      question: '¿Necesito estar presente durante el vaciado?',
      answer: 'No es imprescindible. Muchos clientes nos facilitan las llaves y coordinamos directamente con la portería o administración de fincas. Si prefieres estar presente, también es posible. Lo importante es que tengamos acceso al inmueble y permisos necesarios. Nos adaptamos a tu disponibilidad y preferencias.',
    },
    {
      question: '¿Trabajáis en edificios sin ascensor?',
      answer: 'Sí, trabajamos en cualquier tipo de inmueble, con o sin ascensor. Si no hay ascensor, lo tenemos en cuenta en el presupuesto porque requiere más tiempo y esfuerzo físico. Protegemos siempre las escaleras, pasamanos y zonas comunes con materiales adecuados para evitar daños durante la bajada de muebles.',
    },
    {
      question: '¿Necesito algún permiso especial de la comunidad?',
      answer: 'Normalmente no se necesitan permisos especiales para vaciar un piso, pero sí es recomendable avisar a la portería o administración de fincas con antelación. Nosotros nos coordinamos con ellos para reservar el ascensor si es necesario y asegurar el acceso. En algunos edificios puede ser necesario dejar una fianza por el uso del ascensor, que se devuelve tras el trabajo.',
    },
    {
      question: '¿Qué no incluye el servicio de vaciado?',
      answer: 'El servicio estándar no incluye: limpieza profunda (es opcional), obras o reformas, retirada de instalaciones fijas (aire acondicionado, calentadores), residuos peligrosos (amianto, químicos), ni desmontaje de cocinas o baños completos. Si necesitas alguno de estos servicios adicionales, podemos coordinarlo con un presupuesto aparte. Consulta todos nuestros servicios disponibles.',
    },
    {
      question: '¿Ofrecéis servicio urgente o en fin de semana?',
      answer: 'Sí, ofrecemos servicio urgente según disponibilidad. Si necesitas el vaciado en menos de 48 horas, consúltanos y haremos lo posible por atenderte. También trabajamos en fines de semana si es necesario. Los servicios urgentes o fuera de horario laboral pueden tener un pequeño suplemento que te indicaremos en el presupuesto. Contáctanos para más información.',
    },
    {
      question: '¿Cómo se realiza el pago?',
      answer: 'Normalmente el pago se realiza al finalizar el servicio, una vez compruebes que todo está correcto. Aceptamos efectivo, transferencia bancaria y Bizum. En vaciados muy grandes podemos solicitar un anticipo del 30-40%. Emitimos factura con todos los datos para tu tranquilidad y para que puedas justificar el gasto si lo necesitas.',
    },
    {
      question: '¿Protegéis las zonas comunes del edificio?',
      answer: 'Absolutamente. Protegemos ascensores, escaleras, pasamanos, paredes y suelos con materiales específicos (cartones, mantas, plásticos) para evitar cualquier daño durante el transporte de muebles. Dejamos las zonas comunes tal como las encontramos. Es parte de nuestro compromiso de profesionalidad y respeto hacia la comunidad de vecinos. Conoce más sobre cómo trabajamos.',
    },
    {
      question: '¿Qué zonas de Barcelona cubrís?',
      answer: 'Cubrimos toda Barcelona ciudad (todos los barrios: Eixample, Gràcia, Sants, Sarrià, Les Corts, etc.) y toda el área metropolitana: L\'Hospitalet, Badalona, Santa Coloma, Sant Adrià, Cornellà, El Prat, Sant Cugat, Terrassa, Sabadell, Mataró, Granollers y municipios cercanos. Si tu ubicación no está en la lista, consúltanos igualmente.',
    },
  ];

  const filteredFaqs = faqs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <StructuredData type="faq" />
      <Navbar />
      <main>
        <section className="relative h-[40vh] min-h-[400px] flex items-center overflow-hidden pt-20 md:pt-0">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                'url(https://public.readdy.ai/ai/img_res/e2c6321ed5b67deaa2fba305b831a43c.jpg)',
            }}
          />
          <div className="absolute inset-0 bg-navy/75" />

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center py-8">
            <nav className="text-white/80 text-sm mb-4">
              <a href="/" className="hover:text-white cursor-pointer">Inicio</a>
              <span className="mx-2">/</span>
              <span className="text-white">Preguntas frecuentes</span>
            </nav>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl md:text-6xl font-bold text-white mb-5"
            >
              Preguntas frecuentes
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-white/90 max-w-2xl mx-auto"
            >
              Resuelve tus dudas sobre nuestros servicios de vaciado en Barcelona
            </motion.p>
          </div>
        </section>

        <section className="bg-white py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-12">
              <div className="relative max-w-2xl mx-auto">
                <i className="ri-search-line absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
                <input
                  type="text"
                  placeholder="Buscar pregunta..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full h-14 pl-14 pr-6 border border-gray-300 rounded-full focus:outline-none focus:border-orange focus:ring-2 focus:ring-orange/20 shadow-sm"
                />
              </div>
            </div>

            <div className="space-y-4">
              {filteredFaqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`bg-white border rounded-2xl transition-all cursor-pointer ${
                    activeIndex === index
                      ? 'border-orange shadow-lg bg-gray-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setActiveIndex(activeIndex === index ? null : index)}
                >
                  <div className="flex items-center justify-between p-6">
                    <h3 className="text-lg font-medium text-navy pr-4">{faq.question}</h3>
                    <i
                      className={`ri-arrow-down-s-line text-2xl flex-shrink-0 transition-transform ${
                        activeIndex === index ? 'rotate-180 text-orange' : 'text-gray-400'
                      }`}
                    ></i>
                  </div>

                  <AnimatePresence>
                    {activeIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-6 pt-0">
                          <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>

            {filteredFaqs.length === 0 && (
              <div className="text-center py-12">
                <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
                <p className="text-gray-600">
                  No se encontraron preguntas que coincidan con tu búsqueda.
                </p>
              </div>
            )}
          </div>
        </section>

        <section className="bg-gray-50 py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-navy mb-5">
              ¿No encuentras la respuesta que buscas?
            </h2>
            <p className="text-lg text-gray-600 mb-10">
              Contacta con nosotros directamente y resolveremos todas tus dudas sin compromiso. También puedes conocer más sobre <a href="/empresa" className="text-orange hover:underline font-medium">nuestra empresa</a> y <a href="/servicios" className="text-orange hover:underline font-medium">nuestros servicios</a>.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/34629501941"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-orange text-white text-base font-medium px-8 py-4 rounded-full hover:bg-orange-light hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-xl mr-2"></i>
                Preguntar por WhatsApp
              </a>
              <a
                href="tel:+34629501941"
                className="inline-flex items-center justify-center border-2 border-navy bg-transparent text-navy text-base font-medium px-8 py-4 rounded-full hover:bg-navy hover:text-white transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl mr-2"></i>
                Llamar ahora
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <CookieBanner />
      <ScrollToTop />
    </>
  );
};

export default FaqPage;
